import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { UserMinus, Users, X, Building2, AlertTriangle, Crown, Layers } from "lucide-react";
import { toast } from "sonner";
import { bulkUpdateLeads } from "../../services/crmServices/leadService";
import { useAuth } from "../../hooks/useAuth";

export function BulkUnassignModal({ open, onClose, selectedLeads, onSuccess }) {
    const { user } = useAuth();
    const [clearAgent, setClearAgent] = useState(false);
    const [clearDesk, setClearDesk] = useState(false);
    const [clearFloorManager, setClearFloorManager] = useState(false);
    const [clearGeneralManager, setClearGeneralManager] = useState(false);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });

    const role = user?.position?.role;
    const canUnassignFromDesk = role === "admin" || role === "floorManager";
    const canUnassignFromFloorManager = role === "admin";
    const canUnassignFromGM = role === "admin";

    const nothingSelected = !clearAgent && !clearDesk && !clearFloorManager && !clearGeneralManager;

    // Build dynamic target text from selections
    const getTargetText = () => {
        const parts = [];
        if (clearAgent) parts.push("User");
        if (clearDesk) parts.push("Desk");
        if (clearFloorManager) parts.push("Floor Manager");
        if (clearGeneralManager) parts.push("General Manager");
        return parts.length > 0 ? parts.join(", ") : "...";
    };

    const handleUnassign = async () => {
        if (nothingSelected) {
            toast.error("Please select at least one field to clear");
            return;
        }

        setLoading(true);
        const startTime = Date.now();
        const totalLeads = selectedLeads.length;
        setProgress({ current: 0, total: totalLeads, percentage: 0 });

        const targetText = getTargetText();
        const progressToast = toast.loading(`Preparing to unassign ${totalLeads.toLocaleString()} leads from ${targetText}...`);

        try {
            const leadIds = selectedLeads.map((lead) => lead._id);

            // Build updates object — only null fields for what the user checked
            const updates = {};
            if (clearAgent) updates.agent = null;
            if (clearDesk) updates.desk = null;
            if (clearFloorManager) updates.floorManager = null;
            if (clearGeneralManager) updates.generalManager = null;

            const unassignmentData = { leadIds, updates };

            // Enhanced progress simulation
            let progressStep = 0;
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    progressStep++;
                    const progressValue = Math.min(95, Math.round(100 * (1 - Math.exp(-progressStep / 8))));
                    const currentCount = Math.round((progressValue / 100) * totalLeads);
                    const elapsed = Math.round((Date.now() - startTime) / 1000);
                    const estimated = progressValue > 5 ? Math.round((elapsed / progressValue) * 100) : 0;
                    const remaining = Math.max(0, estimated - elapsed);
                    
                    toast.loading(
                        `Unassigning ${currentCount.toLocaleString()} / ${totalLeads.toLocaleString()} leads (${progressValue}%) • ${remaining}s remaining`,
                        { id: progressToast }
                    );
                    return { current: currentCount, total: totalLeads, percentage: progressValue };
                });
            }, 300);

            const response = await bulkUpdateLeads(unassignmentData);

            clearInterval(progressInterval);
            const elapsed = Math.round((Date.now() - startTime) / 1000);
            toast.dismiss(progressToast);
            setProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (response?.status === 200) {
                const actualCount = response?.data?.count || selectedLeads.length;
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                const successMessage = `Successfully unassigned ${actualCount.toLocaleString()} leads from ${targetText} in ${timeText}`;
                toast.success(successMessage, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                toast.error("Failed to unassign leads");
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Error unassigning leads:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to unassign leads";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleClose = () => {
        setClearAgent(false);
        setClearDesk(false);
        setClearFloorManager(false);
        setClearGeneralManager(false);
        onClose();
    };

    // Build dynamic description of what will remain
    const getWarningItems = () => {
        const items = [];
        if (clearAgent) items.push("User (Agent) assignment will be cleared");
        if (clearDesk) items.push("Desk assignment will be cleared");
        if (clearFloorManager) items.push("Floor Manager assignment will be cleared");
        if (clearGeneralManager) items.push("General Manager assignment will be cleared");

        // What remains
        const remaining = [];
        if (!clearAgent) remaining.push("User");
        if (!clearDesk) remaining.push("Desk");
        if (!clearFloorManager) remaining.push("Floor Manager");
        if (!clearGeneralManager) remaining.push("General Manager");

        if (remaining.length > 0 && remaining.length < 4) {
            items.push(`${remaining.join(", ")} will remain unchanged`);
        }
        if (remaining.length === 0) {
            items.push("Leads will become completely unassigned");
        }

        return items;
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="flex items-center">
                        <UserMinus className="h-5 w-5 mr-2" />
                        Bulk Unassign Leads
                    </DialogTitle>
                    <DialogDescription>
                        Choose which assignments to clear from {selectedLeads.length} selected leads
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 overflow-y-auto flex-1 pr-2 -mr-2">
                    {/* Unassignment Checkboxes */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Select Fields to Clear</CardTitle>
                            <CardDescription>Check which assignments to remove from the selected leads</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col space-y-4">
                                {/* Agent checkbox — always visible */}
                                <div className="flex items-start space-x-3">
                                    <Checkbox
                                        id="clear-agent"
                                        checked={clearAgent}
                                        onCheckedChange={(checked) => setClearAgent(!!checked)}
                                        disabled={loading}
                                    />
                                    <div className="grid gap-0.5 leading-none">
                                        <Label htmlFor="clear-agent" className="text-sm font-medium flex items-center cursor-pointer">
                                            <UserMinus className="h-4 w-4 mr-2 text-blue-600" />
                                            User (Agent)
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Remove the assigned agent from leads
                                        </p>
                                    </div>
                                </div>

                                {/* Desk checkbox */}
                                {canUnassignFromDesk && (
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="clear-desk"
                                            checked={clearDesk}
                                            onCheckedChange={(checked) => setClearDesk(!!checked)}
                                            disabled={loading}
                                        />
                                        <div className="grid gap-0.5 leading-none">
                                            <Label htmlFor="clear-desk" className="text-sm font-medium flex items-center cursor-pointer">
                                                <Building2 className="h-4 w-4 mr-2 text-purple-600" />
                                                Desk
                                            </Label>
                                            <p className="text-xs text-muted-foreground">
                                                Remove the desk assignment from leads
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Floor Manager checkbox */}
                                {canUnassignFromFloorManager && (
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="clear-floor-manager"
                                            checked={clearFloorManager}
                                            onCheckedChange={(checked) => setClearFloorManager(!!checked)}
                                            disabled={loading}
                                        />
                                        <div className="grid gap-0.5 leading-none">
                                            <Label htmlFor="clear-floor-manager" className="text-sm font-medium flex items-center cursor-pointer">
                                                <Layers className="h-4 w-4 mr-2 text-orange-600" />
                                                Floor Manager
                                            </Label>
                                            <p className="text-xs text-muted-foreground">
                                                Remove the floor manager assignment from leads
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* General Manager checkbox */}
                                {canUnassignFromGM && (
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="clear-general-manager"
                                            checked={clearGeneralManager}
                                            onCheckedChange={(checked) => setClearGeneralManager(!!checked)}
                                            disabled={loading}
                                        />
                                        <div className="grid gap-0.5 leading-none">
                                            <Label htmlFor="clear-general-manager" className="text-sm font-medium flex items-center cursor-pointer">
                                                <Crown className="h-4 w-4 mr-2 text-yellow-600" />
                                                General Manager
                                            </Label>
                                            <p className="text-xs text-muted-foreground">
                                                Remove the general manager assignment from leads
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Progress Bar */}
                    {loading && progress.total > 0 && (
                        <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-red-50 shadow-lg">
                            <CardContent className="pt-4">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-3">
                                            <div className="relative">
                                                <UserMinus className="h-5 w-5 text-orange-600 animate-spin" style={{ animationDuration: '2s' }} />
                                                <div className="absolute inset-0 bg-orange-400 rounded-full animate-ping opacity-20"></div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-semibold text-orange-900 block">
                                                    Unassigning leads in progress
                                                </span>
                                                <span className="text-xs text-orange-600">
                                                    Please wait, processing {progress.total.toLocaleString()} leads...
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-bold text-orange-700 block">
                                                {progress.percentage}%
                                            </span>
                                            <span className="text-xs text-orange-600">
                                                {progress.current.toLocaleString()} / {progress.total.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-orange-200 rounded-full h-3 shadow-inner overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-orange-500 to-red-600 h-3 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                                            style={{ width: `${progress.percentage}%` }}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Warning Message */}
                    {!nothingSelected && (
                        <Card className="border-red-200 bg-red-50">
                            <CardContent className="pt-4">
                                <div className="flex items-start space-x-2">
                                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium">Unassignment Details:</p>
                                        <ul className="mt-1 list-disc list-inside space-y-1">
                                            {getWarningItems().map((item, idx) => (
                                                <li key={idx}>{item}</li>
                                            ))}
                                            <li className="font-semibold mt-2">This action cannot be undone</li>
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Action Buttons - Sticky Footer */}
                <div className="flex space-x-3 pt-4 border-t bg-white flex-shrink-0 -mx-6 -mb-6 px-6 pb-6">
                    <Button
                        onClick={handleUnassign}
                        disabled={loading || nothingSelected}
                        className="flex-1 "
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Unassigning...
                            </>
                        ) : (
                            <>
                                <UserMinus className="h-4 w-4 mr-2" />
                                {nothingSelected
                                    ? "Select fields to clear"
                                    : `Unassign ${selectedLeads.length} Leads from ${getTargetText()}`}
                            </>
                        )}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex-1"
                    >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
